import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useActiveCelebrations } from '@/hooks/useActiveCelebrations';
import type { Celebration } from '@/types/celebration.types';

interface CelebrationRibbonProps {
  className?: string;
  /** Controlled list of active celebrations. Falls back to internal hook if omitted. */
  activeCelebrations?: Celebration[];
  loading?: boolean;
  canCreateNew?: boolean;
}

export const CelebrationRibbon: React.FC<CelebrationRibbonProps> = ({
  className = '',
  activeCelebrations: controlledCelebrations,
  loading: controlledLoading,
  canCreateNew: controlledCanCreateNew,
}) => {
  const internal = useActiveCelebrations();
  const location = useLocation();

  const activeCelebrations = controlledCelebrations ?? internal.activeCelebrations;
  const loading = controlledLoading ?? internal.loading;
  const canCreateNew = controlledCanCreateNew ?? internal.canCreateNew;

  // Detect current celebration so its chip is rendered non-clickable
  const currentCelebrationId = React.useMemo(() => {
    const match = location.pathname.match(/\/celebrations\/(\d+)/);
    return match ? parseInt(match[1]) : null;
  }, [location.pathname]);

  if (loading) {
    return (
      <div className={`flex items-center space-x-3 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 w-40 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      {/* All celebrations — current one is non-clickable */}
      {activeCelebrations.map((celebration) => {
        const isCurrent = celebration.id === currentCelebrationId;

        if (isCurrent) {
          return (
            <span
              key={celebration.id}
              className="text-sm font-semibold text-foreground cursor-default whitespace-nowrap"
              aria-current="page"
            >
              {celebration.title}
            </span>
          );
        }

        return (
          <Link
            key={celebration.id}
            to={`/celebrations/${celebration.id}`}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
          >
            {celebration.title}
          </Link>
        );
      })}

      {/* New Celebration button — only when below max */}
      {canCreateNew && (
        <Link to="/celebrations/new" className="group relative">
          <div className="flex items-center space-x-1 px-2 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full border border-blue-200 hover:from-blue-200 hover:to-indigo-200 transition-colors duration-200">
            <Plus className="h-3 w-3 text-blue-600" />
            <span className="text-xs font-medium text-blue-700">New</span>
          </div>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
            New celebration
            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
          </div>
        </Link>
      )}

      {/* Max limit indicator */}
      {!canCreateNew && activeCelebrations.length >= 3 && (
        <span className="text-xs text-amber-700 whitespace-nowrap">Max 3 reached</span>
      )}
    </div>
  );
};
